import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Page, Text, View, Document, StyleSheet, pdf, Line, Svg, Image } from '@react-pdf/renderer';

import { useAppSelector } from '~redux/hooks';

import AffinLogo from '~assets/images/affin-logo-v1.png';
import ActionButton from '~components/atoms/ActionButton';
import { MetalTypeEnum } from '~utils/enums';

type PageProps = {
    children: React.ReactNode;
    title: string;
    subtitle: string;
    copy: string;
}

const SummaryPDF = () => {
    const collateralParams = useAppSelector((state) => state.customer.createCollateralParams);
    const customerDetails = useAppSelector((state) => state.customer.customerDetails);
    const goldPrices = useAppSelector((state) => state.gold.buyAndSellGold.goldPrices);
    const silverPrices = useAppSelector((state) => state.silver.buyAndSellSilver.silverPrices);
    const branchCode = useAppSelector(state => state.auth.userDetails.branchCode);
    const branches = useAppSelector(state => state.branch.allBranches);

    const userBranch = branches.find(branch => branch.branchCode === branchCode);

    const userBranchName = userBranch?.branchName ?? '-';

    const isSilver = collateralParams.metalType === MetalTypeEnum.Silver;

    const metalPrice = isSilver
        ? (collateralParams.silverPrice ?? silverPrices.gbuy)
        : (collateralParams.goldPrice ?? goldPrices.gbuy);

    const [loading, setLoading] = useState(false);
    const [newSummaryTable, setNewSummaryTable] = useState<Array<{ label: string; value: string | number; }>>([]);

    const summaryTable = [
        {
            label: 'Collateral Status / Status Cagaran',
            value: 'Pending',
        },
        {
            label: 'Collateral Date/Time / Tarikh/Masa Cagaran',
            value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
        },
        // {
        //     label: 'Recipient Reference No. / No. Rujukan Penerima',
        //     value: 'TR1234567',
        // },
        {
            label: 'Branch Name / Nama Cawangan',
            value: userBranchName,
        },
        {
            label: 'Name of Customer/Company/Firm/Association / Nama Pelanggan/Syarikat/Firma/Persatuan',
            value: customerDetails.name,
        },
        {
            label: 'Customer ID / ID Pelanggan',
            value: customerDetails.idNo,
        },
        {
            label: 'Product Name / Nama Produk',
            value: collateralParams.productName || '',
        },
        {
            label: 'Price/ Harga',
            value: metalPrice,
        },
        {
            label: 'Collateral Amount (gram)/ Jumlah Cagaran (gram)',
            value: collateralParams.amount,
        },
        {
            label: 'Market Value (RM)/ Harga Pasaran (RM)',
            value: collateralParams.marketValue || '',
        },
        {
            label: 'Collateral Expiry Date / Tarikh Akhir Cagaran',
            value: dayjs(collateralParams.collateralEndDate).format('DD/MM/YYYY hh:mm A'),
        },
    ];

    useEffect(() => {
        if (customerDetails.additionalUserDetails) {
            setNewSummaryTable(
                [
                    {
                        label: 'Collateral Status / Status Cagaran',
                        value: 'Pending',
                    },
                    {
                        label: 'Collateral Date/Time / Tarikh/Masa Cagaran',
                        value: dayjs(new Date()).format('DD/MM/YYYY hh:mm A'),
                    },
                    // {
                    //     label: 'Recipient Reference No. / No. Rujukan Penerima',
                    //     value: 'TR1234567',
                    // },
                    {
                        label: 'Branch Name / Nama Cawangan',
                        value: userBranchName,
                    },
                    {
                        label: 'Name of Customer/Company/Firm/Association / Nama Pelanggan/Syarikat/Firma/Persatuan',
                        value: customerDetails.name,
                    },
                    {
                        label: 'Child\'s name',
                        value: customerDetails.additionalUserDetails[0].name,
                    },
                    {
                        label: 'Customer ID / ID Pelanggan',
                        value: customerDetails.idNo,
                    },
                    {
                        label: 'Product Name / Nama Produk',
                        value: collateralParams.productName || '',
                    },
                    {
                        label: 'Price/ Hanga',
                        value: metalPrice,
                    },
                    {
                        label: 'Market Value (RM)/ Harga Pasaran (RM)',
                        value: collateralParams.marketValue || '',
                    },
                    {
                        label: 'Collateral Expiry Date / Tarikh Akhir Cagaran',
                        value: dayjs(collateralParams.collateralEndDate).format('DD/MM/YYYY hh:mm A'),
                    },
                ]
            );
        }
        else {
            setNewSummaryTable(summaryTable);
        }
    }, [customerDetails, collateralParams.metalType]);

    const productTable = {
        productName: collateralParams.productName,
        pricePerGram: metalPrice,
    };

    const PageWithHeaderFooter = ({
        children,
        title,
        subtitle,
        copy,
    }: PageProps) => (
        <Page size='A4' style={copy === 'Customer\'s Copy' ? styles.customerPage : styles.page} orientation='landscape'>
            {/* Header */}
            <View style={styles.header}>
                {
                    copy === 'Customer\'s Copy' &&
                    <Image
                        style={styles.headerLogo}
                        src={AffinLogo}
                    />
                }
                <View style={styles.headerContent}>
                    <Text style={styles.headerTitle}>{title}</Text>
                    <Text style={styles.headerSubtitle}>{subtitle}</Text>
                </View>
                <Text style={styles.headerCopy}>{copy}</Text>
                <View style={styles.divider} />
            </View>

            {/* Main Content */}
            <View style={styles.mainContent}>
                {children}
            </View>
        </Page>
    );

    const renderDetails = () => {
        return (
            <View style={styles.detailsTable}>
                {newSummaryTable.map((item, index) => (
                    <View style={styles.detailsRow} key={index}>
                        <Text style={styles.detailsCell}>{item.label}</Text>
                        <Text style={styles.detailsCell}>{`: ${item.value}`}</Text>
                    </View>
                ))}
            </View>
        );
    };

    const renderGoldDescription = () => {
        return (
            <View>
                <Text style={styles.goldDescriptionTitle}>Description / Deskripsi</Text>
                <View style={styles.goldTable}>
                    <View style={styles.goldTableRow}>
                        <Text style={styles.goldHeaderCell}>Product / Produk</Text>
                        <Text style={styles.goldHeaderCellPrice}>Price Per Gram / Harga Segram</Text>
                    </View>
                    <View style={styles.goldTableRow}>
                        <Text style={[styles.goldTableCell, { width: '70%' }]}>{productTable.productName}</Text>
                        <Text style={[styles.goldTableCell, { width: '30%' }]}>{productTable.pricePerGram}</Text>
                    </View>
                </View>
            </View>
        );
    };

    const renderDeclaration = () => {
        return (
            <View>
                <View style={styles.declarationContainer2}>
                    <Text style={styles.declarationText}>Note: This summary is computer generated and no signature is required.</Text>
                    <Text style={styles.declarationText}>Nota: Ringkasan ini adalah cetakan komputer dan tandatangan tidak diperlukan.</Text>
                </View>
            </View>
        );
    };

    const handleOpenPDFInNewTab = () => {
        setLoading(true);
        // Create the PDF document

        const docTitle = 'COLLATERAL / CAGARAN';

        const doc = (
            <Document>
                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Customer\'s Copy'}
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDeclaration()}
                </PageWithHeaderFooter>

                <PageWithHeaderFooter
                    title={docTitle}
                    subtitle='SUMMARY DETAILS / RINGKASAN'
                    copy={'Bank\'s Copy'}
                >
                    {renderDetails()}
                    {renderGoldDescription()}
                    {renderDeclaration()}
                </PageWithHeaderFooter>
            </Document>
        );

        // Generate PDF Blob and open in new tab
        pdf(doc).toBlob().then(() => {
            const promise = pdf(doc).toBlob();
            promise.then((blob) => {
                const pdfUrl = URL.createObjectURL(blob);
                setLoading(false);
                window.open(pdfUrl, '_blank');
            }).catch(() => {
                setLoading(false);
            });
        });
    };
    return (
        <div>
            <ActionButton
                label='Print Summary'
                onClick={handleOpenPDFInNewTab}
                isLoading={loading}
            // className={SummaryStyles.container}
            />
        </div>
    );
};

// Create styles
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        paddingTop: 50, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    customerPage: {
        flexDirection: 'column',
        paddingTop: 100, // Space for header
        // paddingBottom: 80, // Space for footer
        paddingHorizontal: 20,
        position: 'relative',
    },
    header: {
        position: 'absolute',
        top: 10,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    headerLogo: {
        width: 162,
        height: 50,
        padding: 5,
    },
    headerContent: {
        flexDirection: 'column',
        gap: 3,
        marginBottom: 5,
    },
    headerTitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerSubtitle: {
        fontSize: 8,
        fontFamily: 'Helvetica-Bold',
    },
    headerCopy: {
        position: 'absolute',
        top: 5,
        right: 20,
        fontSize: 6,
        color: '#666',
    },
    divider: {
        height: 2,
        backgroundColor: '#000',
        marginVertical: 3,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    footerContent: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 10,
    },
    pageNumber: {
        fontSize: 6,
        color: '#666',
    },
    mainContent: {
        flex: 1,
    },
    detailsTable: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 2,
        marginBottom: 15,
    },
    detailsRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    detailsCell: {
        width: '50%',
    },
    goldDescriptionTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    goldTable: {
        width: '60%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    goldTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    goldHeaderCell: {
        width: '70%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldHeaderCellPrice: {
        width: '30%', // Second column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    goldTableCell: {
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
    },
    declarationContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 15,
    },
    declarationContainer2: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: 5,
    },
    declarationTitle: {
        marginBottom: 5,
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
        textDecoration: 'underline',
    },
    declarationText: {
        fontSize: 6,
    },
    declarationTextBold: {
        fontSize: 6,
        fontFamily: 'Helvetica-Bold',
    },
    signatureContainer: {
        display: 'flex',
        width: '100%',
        fontSize: 6,
        gap: 1,
        marginBottom: 20,
    },
    signatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    signatureCell: {
        width: '50%',
    },
    extraSignatureContainer: {
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        fontSize: 6,
        gap: 5,
        marginBottom: 15,
    },
    extraSignature: {
        width: '50%',
    },
    extraSignatureRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    extraSignatureCell: {
        width: '40%',
        maxLines: 1,
    },
    bankTable: {
        width: '100%', // Full width
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: '#000000',
        borderBottomWidth: 0,
        borderRightWidth: 0,
        fontSize: 6,
        marginBottom: 15,
    },
    bankTableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderColor: '#000000',
    },
    bankHeaderCell: {
        width: '100%', // First column
        padding: 1,
        textAlign: 'center',
        borderRightWidth: 1,
        borderColor: '#000000',
        fontWeight: 'bold',
    },
    bankTableCell: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: 4,
        borderRightWidth: 1,
        borderColor: '#000000',
    },
});

export default SummaryPDF;
